import React, { useState } from "react";

/**
 * One address in the Duplicate WorkOrder dialog, as a collapsible section.
 *
 * Closed (the default): the copy checkbox, the label and a one-line summary.
 * Open: the seven typed fields, editable -- pre-filled from the original,
 * because an address IS copied by default and the fields should show exactly
 * what is going over. The server takes only these seven and merges them into
 * the copied address, so UPS details stored with it are kept.
 *
 * Unticked, the address is not copied at all: the fields are disabled.
 */
export const ADDRESS_FIELDS = [
  { name: "addressCompany", label: "Company", className: "col-md-6" },
  { name: "addressLine1", label: "Address Line 1", className: "col-md-6" },
  { name: "addressLine2", label: "Address Line 2", className: "col-md-6" },
  { name: "city", label: "City", className: "col-md-6 col-lg-3" },
  { name: "state", label: "State", className: "col-4 col-lg-1" },
  { name: "zip", label: "Zip", className: "col-4 col-lg-1" },
  { name: "country", label: "Country", className: "col-4 col-lg-1" },
];

// Older rows hold the address as a JSON string, some encoded twice.
export const parseAddress = (value) => {
  let v = value;
  for (let i = 0; i < 3 && typeof v === "string"; i++) {
    try {
      v = JSON.parse(v);
    } catch {
      return {};
    }
  }
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
};

// The seven fields as strings, never null, so every input stays controlled.
export const toAddressForm = (value) => {
  const a = parseAddress(value);
  return Object.fromEntries(ADDRESS_FIELDS.map((f) => [f.name, a[f.name] ?? ""]));
};

const summaryOf = (a) =>
  [
    a.addressCompany,
    a.addressLine1,
    a.addressLine2,
    [a.city, [a.state, a.zip].filter(Boolean).join(" ")].filter(Boolean).join(", "),
  ]
    .filter((s) => String(s || "").trim())
    .join(", ") || "No address on this work order";

export default function DuplicateWOAddress({ id, label, checked, onToggleCopy, value, onChange }) {
  const [open, setOpen] = useState(false);
  const bodyId = `${id}-fields`;

  return (
    <div className="border rounded mb-2">
      <div
        className="d-flex align-items-center gap-2 px-2 py-1"
        style={{ cursor: "pointer" }}
        onClick={(e) => {
          // The checkbox decides copying; everything else on the bar opens it.
          if (e.target.tagName !== "INPUT" && e.target.tagName !== "LABEL") setOpen((o) => !o);
        }}
      >
        <div className="form-check mb-0 text-nowrap">
          <input
            type="checkbox"
            id={id}
            className="form-check-input"
            checked={checked}
            onChange={(e) => onToggleCopy(e.target.checked)}
          />
          <label className="form-check-label fw-semibold" htmlFor={id}>
            {label}
          </label>
        </div>
        <span className={`small text-truncate flex-grow-1 ${checked ? "text-muted" : "text-muted opacity-50"}`}>
          {checked ? summaryOf(value) : "not copied"}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-link text-muted p-0"
          aria-expanded={open}
          aria-controls={bodyId}
          aria-label={`${open ? "Hide" : "Edit"} ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
        >
          <i className={`mdi ${open ? "mdi-chevron-up" : "mdi-chevron-down"} fs-5`}></i>
        </button>
      </div>

      {open && (
        <div id={bodyId} className="border-top px-2 pt-1 pb-2">
          <div className="row g-2">
            {ADDRESS_FIELDS.map((f) => (
              <div className={f.className} key={f.name}>
                <label htmlFor={`${id}-${f.name}`} className="form-label small text-muted mb-0">
                  <span className="visually-hidden">{label} </span>
                  {f.label}
                </label>
                <input
                  id={`${id}-${f.name}`}
                  type="text"
                  className="form-control form-control-sm"
                  value={value[f.name]}
                  disabled={!checked}
                  onChange={(e) => onChange({ ...value, [f.name]: e.target.value })}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
